import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type {
  PassType,
  TargetDataItem,
  TargetDataResponse,
} from "types/BusinessDashboard";
import type { MonthlyParams } from "api/businessDashboard";

import {
  useTargetDataQuery,
  usePostTargetDataMutation,
  usePatchTargetDataMutation,
  dashboardQueryKeys,
} from "queries/Business/Dashboard/useDashboardQueries";

import Popup from "components/ui/Popup";
import Toast from "components/ui/Toast";

import type {
  BepSalesFormValues,
  SalesCategory,
} from "./validators/bep-sales-schema";
import { BepSalesForm } from "./bep-sales-form";
import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";

dayjs.locale("ko");

const StyledPopup = styled(Popup)`
  & > div {
    padding-bottom: 15px;
  }
  .top {
    position: relative;
  }
  .top button {
    all: unset;
    position: absolute;
    right: 0;
    top: 0;
  }

  .middle {
    color: #6b748b;
    font-size: 16px;
    font-weight: 600;
  }
`;

// UI의 SalesCategory를 API의 PassType으로 변환
const categoryToPassType: { [key in SalesCategory]: PassType } = {
  전체: "TOTAL",
  개인: "PERSONAL_LESSON",
  그룹: "GROUP_CLASS",
  회원권: "MEMBERSHIP",
  옵션: "OPTION",
  기타: "ETC",
};

// API의 PassType을 UI의 SalesCategory로 변환
const passTypeToCategory: { [key in PassType]?: SalesCategory } = {
  TOTAL: "전체",
  PERSONAL_LESSON: "개인",
  GROUP_CLASS: "그룹",
  MEMBERSHIP: "회원권",
  OPTION: "옵션",
  ETC: "기타",
};

/**
 * UI 폼 데이터를 API 페이로드 형식으로 변환
 */
export const mapFormValuesToPayload = (
  formValues: BepSalesFormValues
): TargetDataItem[] => {
  return formValues.items.map((item) => ({
    passType: categoryToPassType[item.category],
    targetRevenue: item.targetSales,
    breakEvenPoint: item.bep,
  }));
};

/**
 * API 응답 데이터를 UI 폼 데이터 형식으로 변환
 */
export const mapResponseToFormValues = (
  response: TargetDataResponse
): BepSalesFormValues => {
  return {
    items: response.targetData
      .map((item) => {
        const category = passTypeToCategory[item.passType];
        if (!category) return null; // 매핑되지 않는 PassType은 무시
        return {
          category,
          targetSales: item.targetRevenue,
          bep: item.breakEvenPoint,
        };
      })
      .filter(Boolean) as BepSalesFormValues["items"],
  };
};

const createNewTemplate = (): BepSalesFormValues => {
  const categories: SalesCategory[] = [
    "전체",
    "개인",
    "그룹",
    "회원권",
    "옵션",
    "기타",
  ];
  return {
    items: categories.map((cat) => ({
      category: cat,
      bep: null,
      targetSales: null,
    })),
  };
};

interface BepSalesFormDialogProps {
  open: boolean;
  onClose: () => void;
}

export const BepSalesFormDialog = ({
  open,
  onClose,
}: BepSalesFormDialogProps) => {
  const popupRef = useRef(null);
  const { searchParams } = useUpdateUrlParams();

  const referenceMonth = searchParams.get("month") || dayjs().format("YYYY-MM");

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const targetApiParams = useMemo(() => {
    const params: MonthlyParams = {
      referenceMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, partnerInfo]);

  const {
    data: targetDataResponse,
    isLoading,
    isError,
    error,
  } = useTargetDataQuery(targetApiParams, {
    enabled: true,
  });

  const queryClient = useQueryClient();
  const postMutation = usePostTargetDataMutation();
  const patchMutation = usePatchTargetDataMutation();

  const [initialData, setInitialData] = useState<
    BepSalesFormValues | undefined
  >(undefined);

  // 수정 모드인지 생성 모드인지 동적 결정
  const isEditMode =
    !!targetDataResponse && targetDataResponse.targetData.length > 0;

  useEffect(() => {
    if (isLoading) {
      setInitialData(undefined); // 로딩 중에는 폼을 비활성화하거나 스켈레톤 표시
      return;
    }

    if (isError) {
      console.error("Failed to fetch target data:", error);
      // toast.error("데이터를 불러오는 데 실패했습니다.");
      setInitialData(createNewTemplate()); // 에러 발생 시 새 템플릿으로 초기화
      return;
    }

    if (isEditMode) {
      // 수정 모드: API 응답을 폼 데이터로 변환
      setInitialData(mapResponseToFormValues(targetDataResponse));
    } else {
      // 생성 모드: 새 템플릿으로 초기화
      setInitialData(createNewTemplate());
    }
  }, [isLoading, isError, error, isEditMode, targetDataResponse]);

  const handleFormSubmit = async (values: BepSalesFormValues) => {
    const payload = {
      ...targetApiParams,
      targetData: mapFormValuesToPayload(values),
    };

    const mutation = isEditMode ? patchMutation : postMutation;
    const toastMessage = isEditMode ? "수정" : "생성";

    mutation.mutate(payload, {
      onSuccess: () => {
        Toast(`설정이 성공적으로 저장되었습니다.`, "check", "76px", true);
        queryClient.invalidateQueries({
          queryKey: dashboardQueryKeys.all,
        });
        onClose();
      },
      onError: (err) => {
        console.error(`Failed to ${toastMessage} target data:`, err);
        Toast(
          `설정 저장 중 오류가 발생했습니다. 다시 시도해주세요.`,
          "",
          "76px",
          true
        );
      },
    });
  };

  const isSubmitting = postMutation.isLoading || patchMutation.isLoading;

  return (
    //@ts-expect-error: __
    <StyledPopup ref={popupRef} open={open} onClose={onClose}>
      <div className="middle">
        {isLoading || !initialData ? (
          <div
            className="flex items-center justify-center p-6"
            style={{ minHeight: "240px" }}
          >
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-full border-4 border-t-[#1F6AFF] border-r-[#1F6AFF] border-b-[#E3E5E8] border-l-[#E3E5E8] animate-spin"></div>
              <p className="mt-4 text-[#6C727F] text-sm font-medium">
                정보를 불러오는 중입니다...
              </p>
            </div>
          </div>
        ) : (
          <BepSalesForm
            initialData={initialData}
            isSubmitting={isSubmitting}
            onSubmit={handleFormSubmit}
            onCancel={onClose}
          />
        )}
      </div>
    </StyledPopup>
  );
};
